import React, { useCallback, useState } from 'react';
import { Account } from '../../../../../assets/constant/accountManager.type';
import { logic } from '../../../../../../logic';
import useLoading from '../../../../../../hooks/useLoading';
import CreateAccountModal from './CreateAccountModal';
import KitContainer from '../../KitContainer';
import PageLoading from '../../../../../components/_atom/loading/PageLoading';
import EmptyDataTemplate from '../../../../dashboard/components/EmptyDataTemplate';
import BasicTable from '../../../../../components/_atom/table/BasicTable';
import { PagingBar } from '../../../../../components/_atom/paging/PagingBar';
import BasicButton from '../../../../../components/_atom/button/BasicButton';
import { ButtonSize, ButtonTheme } from '../../../../../components/_atom/button/StyledButton';
import toast from 'react-hot-toast';
import { formatDateToLocal } from '../../../../../../utils/TimeUtils';
import { ReactComponent as IconCopy } from '../../../../../assets/images/icon_copy.svg';
import OwnerBadge from '../../OwnerBadge';
import { KindOfKit, Kit } from '../../../../../assets/constant/kit.type';
import CopyToClipboard from '../../CopyToClipboard';
import AccountDepositCoinModal from './AccountDepositCoinModal';
import depositIcon from '../../../../../assets/images/atom/icon_deposit.png';

export const ACCOUNT_COLGROUP = [2.5, 1.5, 3, 1, 1];
export const ACCOUNT_HEADERS = [
  { title: 'Account ID', accessor: 'id' },
  { title: 'Account Name', accessor: 'name' },
  { title: 'Account Address', accessor: 'address' },
  { title: 'Created Date', accessor: 'createdAt' },
  { title: 'Deposit Coin', accessor: 'deposit' },
];
export const ACCOUNTS_LIMIT = 10;

const AccountManager = (props: { kitDetail?: Kit; kindOfKit?: KindOfKit }) => {
  const { kitDetail } = props;
  const kitOwnerAddress = kitDetail?.admin_address;
  const [selectedAccount, setSelectedAccount] = useState<Account>();
  const [isOpenDepositModal, setIsOpenDepositModal] = useState(false);
  const ACCOUNT_RENDERER = useCallback(
    (data: Account) => {
      const isKitOwnerAccount = data.address === kitOwnerAddress;
      return {
        id: (
          <div style={{ display: 'flex', gap: 10, paddingRight: 10 }}>
            <div
              title={data.account_id}
              style={{
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {data.account_id}
            </div>
            <CopyToClipboard
              text={data.account_id}
              onCopy={(e) => {
                e.stopPropagation();
                toast('Copied ID', { icon: '👏' });
              }}
            >
              <IconCopy style={{ minWidth: 20, minHeight: 20, objectFit: 'contain', cursor: 'pointer' }} className="icon-copy" />
            </CopyToClipboard>
          </div>
        ),
        name: (
          <div style={{ display: 'flex', gap: 10, paddingRight: 10, alignItems: 'center' }}>
            <div
              title={data.account_id}
              style={{
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {data.name}
            </div>
            {isKitOwnerAccount && <OwnerBadge />}
          </div>
        ),
        address: (
          <div style={{ display: 'flex', gap: 10, paddingRight: 10 }}>
            <div
              title={data.address}
              style={{
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {data.address}
            </div>
            <CopyToClipboard
              text={data.address}
              onCopy={(e) => {
                e.stopPropagation();
                toast('Copied Address', { icon: '👏' });
              }}
            >
              <IconCopy style={{ minWidth: 20, minHeight: 20, objectFit: 'contain', cursor: 'pointer' }} className="icon-copy" />
            </CopyToClipboard>
          </div>
        ),
        createdAt: formatDateToLocal(data.created_at),
        deposit: <img src={depositIcon} alt={'deposit'} style={{ width: 24, height: 24, objectFit: 'contain', borderRadius: 50 }} />,
      };
    },
    [kitOwnerAddress]
  );

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [maxAccountsNum, setMaxAccountsNum] = useState(0);
  const getAccounts = async (option?: { lastId?: string; offset?: number; order?: 'ASC' | 'DESC' }) => {
    const { result, error } = await logic.accountManager.getAccountManagerList({
      limit: ACCOUNTS_LIMIT,
      ...option,
    });
    if (error) {
      return;
    }
    const { rows: accounts, count: maxAccountsNum } = result;
    setAccounts(accounts);
    setMaxAccountsNum(maxAccountsNum);
  };
  const [isOpenRegisterAccountModal, setIsOpenRegisterAccountModal] = useState(false);
  const pageNum = Number(new URLSearchParams(window.location.search).get('account')) || 1;
  const maxPageNum = maxAccountsNum % ACCOUNTS_LIMIT === 0 ? maxAccountsNum / ACCOUNTS_LIMIT : Math.floor(maxAccountsNum / ACCOUNTS_LIMIT) + 1;
  const [refresh, setRefresh] = useState(false);
  const { loading } = useLoading({
    dependency: [pageNum, refresh],
    synchronousFunction: async () => {
      await getAccounts({ offset: (pageNum - 1) * ACCOUNTS_LIMIT });
    },
  });

  return (
    <div className="tab-content-wrapper">
      <CreateAccountModal
        isOpen={isOpenRegisterAccountModal}
        closeModal={() => setIsOpenRegisterAccountModal(false)}
        refreshAccounts={() => setRefresh(!refresh)}
      />
      <AccountDepositCoinModal account={selectedAccount} isOpen={isOpenDepositModal} closeModal={() => setIsOpenDepositModal(false)} />
      <KitContainer
        title={'Account List'}
        Button={
          <BasicButton size={ButtonSize.DYNAMIC} theme={ButtonTheme.PRIMARY} onClick={() => setIsOpenRegisterAccountModal(true)}>
            + New Account
          </BasicButton>
        }
      >
        {loading ? (
          <PageLoading style={{ height: 477 }} />
        ) : (
          <>
            {accounts.length === 0 ? (
              <EmptyDataTemplate>
                <div style={{ fontSize: 18, fontWeight: 500, lineHeight: '21px', color: '#7D828D' }}>There are no registered accounts yet.</div>
              </EmptyDataTemplate>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                }}
              >
                <BasicTable
                  data={accounts}
                  colGroup={ACCOUNT_COLGROUP}
                  headers={ACCOUNT_HEADERS}
                  renderer={ACCOUNT_RENDERER}
                  onClick={(account: Account) => {
                    setSelectedAccount(account);
                    setIsOpenDepositModal(true);
                  }}
                />
                <PagingBar maxPageNum={maxPageNum} query={'account'} />
              </div>
            )}
          </>
        )}
      </KitContainer>
    </div>
  );
};

export default AccountManager;
